// src/components/Resources/Grid.tsx

import React, { FC, useEffect, useMemo, useState } from 'react';
import { ContentfulClientApi } from 'contentful';
import type { Asset } from 'contentful';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { BlogPosts } from './Data';

// Helper to normalize an asset reference into an ID string (no `any` casts)
function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function getAssetId(input: unknown): string | null {
  if (!input) return null;
  if (typeof input === 'string' || typeof input === 'number') return String(input);

  if (isRecord(input)) {
    const sys = (input as Record<string, unknown>)['sys'];
    if (isRecord(sys)) {
      const id = (sys as Record<string, unknown>)['id'];
      if (typeof id === 'string' || typeof id === 'number') {
        return String(id);
      }
    }
  }
  return null;
}

// Narrow the shape we actually use from BlogPosts
export type BlogItem = BlogPosts & {
  id: string | number;
  title: string;
  imageURL: string | number; // Contentful asset ID we can coerce to string
  tags?: string[];
  category?: string;
  author?: string;
  authorPicture?: string | number;
  authorImageURL?: string | number;
  date?: string;
  readTime?: string;
  excerpt?: string;
};

interface Props {
  data: BlogItem[];
  client: ContentfulClientApi;
}

// ===============
// Layout wrappers
// ===============
const Section = styled.section`
  text-align: left;
  margin: 2.5rem auto 4rem;
  max-width: 1200px;
  padding: 0 1rem;

  @media (min-width: 768px) {
    padding: 0 2rem;
  }
`;

const PageHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const Eyebrow = styled.span`
  font-size: 0.875rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #667085;
`;

const H1 = styled.h1`
  font-size: clamp(2rem, 2.6vw, 3rem);
  line-height: 1.1;
  margin: 0;
`;

const Sub = styled.p`
  margin: 0;
  color: #475467;
`;

// ===============
// Search + filters
// ===============
const ControlsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;

  @media (min-width: 720px) {
    grid-template-columns: 1fr auto;
    align-items: center;
  }
`;

const SearchBox = styled.input`
  width: 100%;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  padding: 0.85rem 1rem;
  font-size: 1rem;
  outline: none;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
  &:focus {
    border-color: #b2ddff;
    box-shadow: 0 0 0 4px rgba(26, 86, 219, 0.12);
  }
`;

const Pills = styled.div`
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
`;

const Pill = styled.button<{active?: boolean}>`
  white-space: nowrap;
  border: 1px solid ${({active}) => (active ? '#1d4ed8' : '#e4e7ec')};
  background: ${({active}) => (active ? '#eff6ff' : '#fff')};
  color: ${({active}) => (active ? '#1d4ed8' : '#344054')};
  border-radius: 999px;
  padding: 0.5rem 0.9rem;
  font-size: 0.9rem;
  cursor: pointer;
`;


const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
  color: #667085;
  font-size: 0.9rem;
`;

// Updated CardMedia styled component with $shape prop
const CardMedia = styled.div<{ $shape?: 'square' | 'landscape' | 'portrait' }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ $shape }) => ($shape === 'landscape' ? '16 / 9' : $shape === 'portrait' ? '4 / 5' : '1 / 1')};
  background: #f2f4f7;
  overflow: hidden;
  display: block;
  background-color: #f2f4f7;
`;

const CardBody = styled.div`
  padding: 0.9rem 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

// Updated ImgFill styled component with object-fit: cover
const ImgFill = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover; /* fill frame and avoid side bezels */
  object-position: center;
  transition: transform 0.3s ease;

  ${CardMedia}:hover & {
    transform: scale(1.03);
  }
`;

// ===============
// Card grid
// ===============
const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.25rem;
`;

const CardLink = styled(Link)`
  display: flex;
  flex-direction: column;
  height: 100%;
  text-decoration: none;
  color: inherit;
`;

const BlogCard = styled.article`
  display: flex;
  flex-direction: column;
  border: 1px solid #e2e2e2;
  border-radius: 12px;
  background-color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.08);
  }
`;

const BlogTitle = styled.h2`
  font-size: 1.1rem;
  line-height: 1.35;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Excerpt = styled.p`
  color: #475467;
  margin: 0;
  font-size: 0.95rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardMetaRow = styled(MetaRow)`
  margin-top: auto;
  gap: 0.5rem 0.75rem;
`;

const LoadMore = styled.button`
  margin: 1.5rem auto 0;
  display: block;
  border: 1px solid #e4e7ec;
  background: #fff;
  padding: 0.7rem 1rem;
  border-radius: 10px;
  cursor: pointer;
`;

const Empty = styled.div`
  padding: 3rem 1rem;
  text-align: center;
  color: #667085;
`;

const Tag = styled.span`
  display: inline-block;
  border: 1px solid #e4e7ec;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.8rem;
  color: #475467;
  background: #fff;
  background-clip: padding-box;
`;

// Add new type for asset shapes
type AssetShape = 'square' | 'landscape' | 'portrait';

const Grid: FC<Props> = ({ data, client }) => {
  const [assets, setAssets] = useState<{ [id: string]: string }>({});
  const [shapes, setShapes] = useState<{ [id: string]: AssetShape }>({});
  const [query, setQuery] = useState('');
  const [activeTag, setActiveTag] = useState<string>('All');
  const [visible, setVisible] = useState(9);

  // Fetch Contentful assets
  useEffect(() => {
    data.forEach((post) => {
      const assetId = getAssetId(post.imageURL);
      if (assetId) {
        client
          .getAsset(assetId)
          .then((asset: Asset) => {
            const file = asset.fields.file as { url?: string; details?: { image?: { width?: number; height?: number } } } | undefined;
            let url = file?.url;
            if (typeof url === 'string' && url.length > 0) {
              if (url.startsWith('//')) url = `https:${url}`;
              setAssets((prev) => ({ ...prev, [String(post.id)]: url as string }));
            }
            const dims = file?.details?.image;
            if (dims && typeof dims.width === 'number' && typeof dims.height === 'number' && dims.width > 0 && dims.height > 0) {
              const ratio = dims.width / dims.height;
              const shape: AssetShape = ratio > 1.2 ? 'landscape' : ratio < 0.9 ? 'portrait' : 'square';
              setShapes((prev) => ({ ...prev, [String(post.id)]: shape }));
            }
          })
          .catch((err: unknown) => {
            // eslint-disable-next-line no-console
            console.error('Error fetching asset:', err);
          });
      }
      const authorId = getAssetId(post.authorPicture);
      if (authorId) {
        client
          .getAsset(authorId)
          .then((asset: Asset) => {
            const file = asset.fields.file as { url?: string } | undefined;
            let url = file?.url;
            if (typeof url === 'string' && url.length > 0) {
              if (url.startsWith('//')) url = `https:${url}`;
              setAssets((prev) => ({ ...prev, [`author-${String(post.id)}`]: url as string }));
            }
          })
          .catch((err: unknown) => {
            // eslint-disable-next-line no-console
            console.error('Error fetching author asset:', err);
          });
      }
    });
  }, [data, client]);

  // Sort newest first
  const sortedData = useMemo(() => [...data].sort((a, b) => Number(b.id) - Number(a.id)), [data]);

  // Compute unique tags/categories if present
  const allTags = useMemo(() => {
    const base = new Set<string>();
    sortedData.forEach((p) => {
      if (Array.isArray(p.tags)) p.tags.forEach((t) => base.add(t));
      if (p.category) base.add(p.category);
    });
    return ['All', ...Array.from(base)];
  }, [sortedData]);

  // Search + tag filter
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sortedData.filter((p) => {
      const inTag = activeTag === 'All' || (Array.isArray(p.tags) && p.tags.includes(activeTag)) || p.category === activeTag;
      if (!inTag) return false;
      if (!q) return true;
      const hay = [p.title, p.excerpt ?? '', p.author ?? '', p.category ?? '', Array.isArray(p.tags) ? p.tags.join(' ') : '']
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [sortedData, query, activeTag]);

  const items = filtered;

  return (
    <Section>
      {items.length === 0 ? (
        <Empty>No results found. Try clearing filters.</Empty>
      ) : (
        <BlogGrid>
          {items.slice(0, visible).map((post) => (
            <CardLink key={post.id} to={`/blog/${String(post.id)}`}>
              <BlogCard>
                <CardMedia $shape={shapes[String(post.id)]}>
                  {assets[String(post.id)] && (
                    <ImgFill src={assets[String(post.id)]} alt={post.title} />
                  )}
                </CardMedia>
                <CardBody>
                  {post.category && <Tag>{post.category}</Tag>}
                  <BlogTitle>{post.title}</BlogTitle>
                  {post.excerpt && <Excerpt>{post.excerpt}</Excerpt>}
                  <CardMetaRow>
                    {assets[`author-${String(post.id)}`] && (
                      <img
                        src={assets[`author-${String(post.id)}`]}
                        alt={post.author || 'Author photo'}
                        style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' }}
                      />
                    )}
                    {post.author && <span>{post.author}</span>}
                    {post.date && (
                      <span>
                        • {new Date(post.date).toLocaleDateString('en-US', {
                          month: '2-digit',
                          day: '2-digit',
                          year: 'numeric'
                        })}
                      </span>
                    )}
                    {post.readTime && <span>• {post.readTime} read</span>}
                  </CardMetaRow>
                </CardBody>
              </BlogCard>
            </CardLink>
          ))}
        </BlogGrid>
      )}

      {items.length > visible && (
        <LoadMore onClick={() => setVisible((v) => v + 9)}>Load more</LoadMore>
      )}
    </Section>
  );
};

export default Grid;
